<template>
  <section>
    <TableFilterView
      :columns="columns"
      :initial-filters="activeFilters"
      page="etablissementList"
      :show-export="true"
      :can-export-csv="false"
      :can-export-xls-all="false"
      @update-active-filters="updateActiveFilters"
      @update-visible-columns="updateVisibleColumns"
      @export-xls="exportEtablissementsXls"
    />
    <b-table
      :ref="tableRef"
      :striped="config.isStriped"
      :hoverable="config.isHoverable"
      :debounce-search="config.searchDebounce"
      :aria-next-label="config.nextLabel()"
      :aria-previous-label="config.previousLabel()"
      :aria-page-label="config.pageLabel()"
      :aria-current-label="config.currentPageLabel()"
      :per-page="config.perPage"
      :sticky-header="config.stickyHeader"
      :current-page="currentPagedSortedParameter.pageNumber"
      :pagination-position="config.paginationPosition"
      paginated
      backend-pagination
      :sort-icon="config.sortIcon"
      :sort-icon-size="config.sortIconSize"
      backend-sorting
      sort-multiple
      :sort-multiple-data="buefySortingPriority"
      :total="total"
      :data="etablissementsList"
      :loading="isLoading"
      @sort="onSort"
      @sorting-priority-removed="sortingPriorityRemoved"
      @page-change="onPageChange"
    >
      <b-table-column
        v-for="column in columns"
        :key="column.field"
        v-bind="column"
        :visible="column.visible"
      >
        <template #default="props">
          <div v-if="column.field === 'codeDepartement'">
            <div>{{ props.row["codeDepartement"].replace(/^0+/, "") }}</div>
          </div>
          <div v-if="column.field === 'grae.region'">
            <span v-if="props.row['grae']">{{ props.row["grae"].region }}</span>
          </div>
          <div
            v-if="!column.custom"
            class="clickable"
            @click="showEtablissementListesProjetsPage($event, props.row['id'])"
          >
            <div>{{ props.row[column.field] }}</div>
          </div>
        </template>
      </b-table-column>
    </b-table>
  </section>
</template>

<script lang="ts">
import { EtablissementDTO, FilterDTO, PaginationOrder } from "@/model/bean/GeneratedDTOs"
import { GraeService } from "@/services/rest/grae/GraeService"
import { OrganismeService } from "@/services/rest/organisme/OrganismeService"
import { defineComponent } from "vue"
import Column from "@/views/tables/Column"
import TableFilterView from "@/views/tables/TableFilterView.vue"
import { useTableView } from "@/hooks/useTableView"
import { SortOrder } from "@/model/SortOrder"
import { BTable, BTableColumn } from "buefy"

const HISTORY_PAGINATION_GRAE_ETABLISSEMENT = "history.pagination.grae.etablissement"
export const URL_PREFIX_GRAE_ETABLISSEMENT = "/etablissement/"

export default defineComponent({
  name: "GraeEtablissementsView",
  components: {
    TableFilterView,
    BTable,
    BTableColumn,
  },
  props: {
    graeId: {
      type: String,
      default: undefined,
    },
  },
  data() {
    return {
      graeService: GraeService.INSTANCE,
      organismeService: OrganismeService.INSTANCE,

      etablissementsList: [] as Array<EtablissementDTO>,
      graeNamesList: [] as Array<string>,

      // Utilisation du hook useTableView - sera initialisé dans created
      tableView: null as any,
    }
  },
  computed: {
    // Propriétés réactives exposées par le hook
    tableRef(): string {
      return this.tableView?.state.tableRef || "grae-etablissements-table"
    },
    isLoading(): boolean {
      return this.tableView?.state.isLoading || false
    },
    columns(): Array<Column> {
      return this.tableView?.state.columns || []
    },
    currentPagedSortedParameter(): any {
      return this.tableView?.state.currentPagedSortedParameter
    },
    buefySortingPriority(): Array<any> {
      return this.tableView?.state.buefySortingPriority || []
    },
    total(): number {
      return this.tableView?.state.total || 0
    },
    config(): any {
      return this.tableView?.state.config
    },
    perPage(): number {
      return this.tableView?.state.perPage || 25
    },
    activeFilters(): Array<any> {
      return this.tableView?.state.activeFilters || []
    },
  },
  created() {
    // Initialisation du tableView
    this.tableView = useTableView({
      tableRef: "grae-etablissements-table",
      router: this.$router,
      route: this.$route,
      i18n: this.$i18n,
      getInitialColumns: this.getInitialColumns.bind(this),
      getDefaultSort: this.getDefaultSort.bind(this),
      getInitialActiveFilters: this.getInitialActiveFilters.bind(this),
      reloadData: this.reloadData.bind(this),
      reloadDataWhenMounted: true,
      updateLocalStorageHistoryPagination: this.updateLocalStorageHistoryPagination.bind(this),
    })

    // Logique métier d'initialisation
    if (!this.graeId) {
      this.organismeService.getAllGraes().then((graes) => {
        this.graeNamesList = graes.map((g) => g.region)
        this.refreshInitialColumns()
      })
    }
  },
  mounted() {
    this.setPreviousPagination(
      ["/ae/", URL_PREFIX_GRAE_ETABLISSEMENT],
      HISTORY_PAGINATION_GRAE_ETABLISSEMENT,
    )
    this.tableView?.initMounted()
  },
  methods: {
    // Méthodes déléguées au hook
    updateVisibleColumns(newVisibleColumnList: Array<Column>): void {
      this.tableView?.updateVisibleColumns(newVisibleColumnList)
    },
    updateActiveFilters(newActiveFilters: Array<FilterDTO>): void {
      this.tableView?.updateActiveFilters(newActiveFilters)
    },
    onPageChange(newPage: string | number): void {
      const pageNum = typeof newPage === "string" ? parseInt(newPage) : newPage
      this.tableView.onPageChange(pageNum)
    },

    onSort(field: string | undefined, order: SortOrder): void {
      if (field) {
        this.tableView.onSort(field, order)
      }
    },

    sortingPriorityRemoved(field: string | undefined): void {
      if (field) {
        this.tableView.sortingPriorityRemoved(field)
      }
    },
    setPreviousPagination(activePrefixUrls: string[], historyPaginationStorageKey: string): void {
      this.tableView?.setPreviousPagination(activePrefixUrls, historyPaginationStorageKey)
    },
    setLocalStorageHistoryPagination(historyPaginationStorageKey: string): void {
      this.tableView?.setLocalStorageHistoryPagination(historyPaginationStorageKey)
    },
    refreshInitialColumns(): void {
      this.tableView?.refreshInitialColumns()
    },
    updateTotal(total: number): void {
      this.tableView?.updateTotal(total)
    },

    // Méthodes pour remplacer les setters
    setIsLoading(value: boolean): void {
      if (this.tableView) {
        this.tableView.state.isLoading = value
      }
    },
    setActiveFilters(value: Array<any>): void {
      if (this.tableView) {
        this.tableView.state.activeFilters = value
      }
    },

    // Méthodes callbacks requises par le hook
    updateLocalStorageHistoryPagination(): void {
      this.setLocalStorageHistoryPagination(HISTORY_PAGINATION_GRAE_ETABLISSEMENT)
    },

    // Méthodes métier spécifiques au composant
    getInitialActiveFilters(): Array<FilterDTO> {
      return new Array<FilterDTO>()
    },
    async reloadData(): Promise<void> {
      try {
        const paginatedEtablissementsList = await this.graeService.getEtablissements(
          this.graeId,
          this.currentPagedSortedParameter,
          this.activeFilters,
        )
        this.etablissementsList = paginatedEtablissementsList.elements
        this.updateTotal(paginatedEtablissementsList.count)

        // Get region lists for filtering purposes
        if (!this.graeId) {
          this.organismeService.getAllGraes().then((graes) => {
            this.graeNamesList = graes.map((g) => g.region)
            this.refreshInitialColumns()
          })
        }
      } catch (e: any) {
        // Silent catch
      }
    },
    exportEtablissementsXls(): void {
      this.organismeService.exportEtablissementXls(this.graeId, this.activeFilters)
    },
    getInitialColumns(): Array<Column> {
      const initialColumns: Array<Column> = [
        {
          field: "codeDepartement",
          label: this.$t("graePage.details.etablissements.departement").toString(),
          sortable: true,
          backEndSearchable: false,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "nom",
          label: this.$t("graePage.details.etablissements.nom").toString(),
          sortable: true,
          backEndSearchable: true,
          visible: true,
          custom: false,
          canBeAdded: true,
        },
        {
          field: "ville",
          label: this.$t("graePage.details.etablissements.ville").toString(),
          sortable: false,
          backEndSearchable: false,
          visible: true,
          custom: false,
          canBeAdded: true,
        },
        {
          field: "telephone",
          label: this.$t("graePage.details.etablissements.telephone").toString(),
          sortable: true,
          backEndSearchable: false,
          visible: true,
          custom: false,
          canBeAdded: true,
        },
        {
          field: "courriel",
          label: this.$t("graePage.details.etablissements.courriel").toString(),
          sortable: true,
          backEndSearchable: false,
          visible: true,
          custom: false,
          canBeAdded: true,
        },
        {
          field: "nbProjets",
          label: this.$t("graePage.details.etablissements.nbProjets").toString(),
          sortable: false,
          backEndSearchable: false,
          visible: true,
          custom: false,
          canBeAdded: true,
        },
        {
          field: "codeEtablissement",
          label: this.$t("graePage.details.etablissements.codeEtablissement").toString(),
          sortable: false,
          backEndSearchable: true,
          visible: false,
          custom: false,
          canBeAdded: true,
        },
      ]
      if (!this.graeId) {
        initialColumns.push({
          field: "grae.region",
          label: this.$t("graeList.region").toString(),
          possibleSearchValues: this.graeNamesList,
          sortable: true,
          backEndSearchable: true,
          visible: true,
          custom: true,
          canBeAdded: true,
        })
      }
      return initialColumns
    },

    getDefaultSort(): Array<PaginationOrder> {
      const defaultSort = new PaginationOrder()
      defaultSort.clause = "nom"
      defaultSort.desc = true
      return [defaultSort]
    },

    showEtablissementListesProjetsPage(event: Event, id: number): void {
      // Do not forward click event to row (would trigger modal)
      event.stopPropagation()
      this.$router.push(URL_PREFIX_GRAE_ETABLISSEMENT + id + "/projets")
    },
  },
})
</script>
